import { DataAmigavel } from '@/shared/utils/datas';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { styles } from './styles/estagiario-por-setor.style';
import { IFullReport, IReportComarca, IReportPredio } from '@/api.types';

interface IProps {
  relatorio: IFullReport;
  exibirEventos: boolean;
}

function RenderizarPrediosDaComarca(comarca: IReportComarca, relatorio: IFullReport) {
  const { pessoas, lotacoes } = relatorio;
  const elementos: JSX.Element[] = [];

  comarca.predios.forEach((predio: IReportPredio, index: number) => {
    // Buscar setores deste prédio através de comarca.locais (filtrando por predio_id)
    // Assumindo que locais com predio_id correspondente são setores ou defensorias
    // Vamos filtrar apenas os que têm lotações de estagiários para identificar setores
    const locaisDoPredio = (comarca.locais || []).filter((local) => local.predio_id === predio.id);

    // Filtrar apenas locais que são setores (não têm vara_id)
    // Setores geralmente não têm vara_id, enquanto defensorias têm
    // E devem ter lotações relacionadas através de setor_id
    const setoresDoPredio = locaisDoPredio.filter((local) => {
      // Se não tem vara_id, provavelmente é um setor
      // E verificar se há lotações relacionadas a este setor
      return !local.vara_id && lotacoes.some((l) => {
        return (l as any).setor_id === local.id;
      });
    });

    elementos.push(
      <View key={`predio-${index}`} style={styles.section} wrap={false}>
        <View style={[styles.fullRow, styles.fullCellGrey1]}>
          <Text style={styles.fullCell}>{predio.nome}</Text>
        </View>
        <View style={[styles.fullRow, styles.fullCellGrey]}>
          <Text style={[styles.fullCell, { textAlign: 'right', fontSize: 8, fontStyle: 'italic' }]}>
            {`${predio.endereco?.logradouro || ''}, nº ${predio.endereco?.numero || ''}, ${predio.endereco?.bairro || ''}. CEP: ${predio.endereco?.cep || ''}. ${predio.endereco?.cidade || ''}`}
          </Text>
        </View>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableColHeader, styles.tableColHeader1]}>SETOR DE ATUAÇÃO</Text>
            <Text style={[styles.tableColHeader, styles.tableColHeader2]}>RESPONSÁVEL</Text>
            <Text style={[styles.tableColHeader, styles.tableColHeader3]}>ESTAGIÁRIO(A)</Text>
          </View>

          {setoresDoPredio.map((local, idx: number) => {
            // Buscar lotações de estagiários relacionadas a este setor específico usando setor_id
            const lotacoesEstagiarios = lotacoes.filter((l) => {
              return l.estagio_id && (l as any).setor_id === local.id;
            });

            // Buscar responsável através de lotação de defensor no mesmo setor
            const lotacaoResponsavel = lotacoes.find(
              (l) => l.defensor_id && !l.estagio_id && (l as any).setor_id === local.id
            );
            const responsavelId = lotacaoResponsavel?.defensor_id;
            const responsavel = pessoas.find((p) => p.id === responsavelId)?.nome || local.nome || 'Sem responsável';

            const estagiarios = lotacoesEstagiarios
              .map((lotacao) => {
                const estagiario = pessoas.find((p) =>
                  p.estagios?.some((e) => e.id === lotacao.estagio_id)
                );
                return estagiario
                  ? {
                    nome: estagiario.nome,
                    data_inicial: lotacao.data_inicial,
                    data_final: lotacao.data_final,
                  }
                  : null;
              })
              .filter(Boolean);

            return (
              <View
                key={`setor-${idx}`}
                style={[styles.tableRow, idx % 2 === 0 ? styles.tableRowOdd : styles.tableRowEven]}
                wrap={false}
              >
                <View style={[styles.tableCol, styles.tableCol1]}>
                  <Text>{local.nome}</Text>
                </View>
                <View style={[styles.tableCol, styles.tableCol2]}>
                  <Text>{responsavel}</Text>
                </View>
                <View style={[styles.tableCol, styles.tableCol3]}>
                  {estagiarios.length > 0 ? (
                    <View style={styles.subTable}>
                      {estagiarios.map((e: any, i: number) => (
                        <View key={`e-${i}`} style={styles.subTableRow}>
                          <Text>
                            {e.nome}
                            <Text style={styles.textMuted}>
                              {` (${DataAmigavel(e.data_inicial)} a ${DataAmigavel(e.data_final)})`}
                            </Text>
                          </Text>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <Text style={styles.emptyText}>(VAGO)</Text>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </View>
    );
  });

  return elementos;
}

export function EstagiarioPorSetor({ relatorio }: IProps) {
  if (!relatorio?.comarcas || relatorio.comarcas.length === 0) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </Text>
          <Text style={styles.subtitle}>
            SUBDEFENSORIA - REVISADA EM {DataAmigavel(new Date())}
          </Text>
          <Text style={styles.emptyText}>Nenhum dado disponível</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {relatorio.comarcas.map((comarca: IReportComarca, index: number) => (
        <Page
          key={index}
          size="A4"
          style={styles.page}
          orientation="portrait"
          wrap
        >
          <Text style={styles.title}>
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </Text>
          <Text style={styles.subtitle}>
            SUBDEFENSORIA - REVISADA EM {DataAmigavel(new Date())}
          </Text>
          <View style={styles.section}>
            <View style={[styles.fullRow, styles.fullCellGrey]}>
              <Text style={[styles.fullCell, styles.comarcaHeader]}>{comarca.nome}</Text>
            </View>
            {RenderizarPrediosDaComarca(comarca, relatorio)}
          </View>
          <Text
            style={{
              position: 'absolute',
              fontSize: 10,
              bottom: 10,
              left: 0,
              right: 0,
              textAlign: 'center',
            }}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
            fixed
          />
        </Page>
      ))}
    </Document>
  );
}